import type { JWT } from '@/core/data/auth/auth.interface';
import type { JwtRepository } from '@/core/data/auth/jwt.repository';
import { createLogger } from '@/core/helpers/logger.helper';
import type { GamRequestConfig } from '@/core/network/http/httpClient.interface';
import { limitToOnlyOneRunningPromise } from '@/core/network/http/promises';
import * as Sentry from '@sentry/vue';
import fetch from 'cross-fetch';

const logger = createLogger('JWT Service');

function addSessionBreadcrumb(message: string, level: Sentry.SeverityLevel, data?: Record<string, unknown>): void {
  Sentry.addBreadcrumb({ category: 'session', message, level, data });
}

export class JwtService {
  private readonly repository: JwtRepository;
  private readonly serviceUrl: string;
  private readonly refreshTokenEndpoint = '/v1/auth/refresh';
  private refreshTokenPromise: (() => Promise<void>) | null = null;

  constructor(repository: JwtRepository, serviceUrl: string) {
    this.repository = repository;
    this.serviceUrl = serviceUrl;
  }

  async refreshToken(): Promise<void> {
    if (!this.refreshTokenPromise) {
      this.refreshTokenPromise = limitToOnlyOneRunningPromise(async (): Promise<void> => {
        const tokenInfo = this.getTokenInfo();
        if (!tokenInfo) {
          this.refreshTokenPromise = null;
          return;
        }

        const url = `${this.serviceUrl}${this.refreshTokenEndpoint}`;

        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 30000);

          const response: Response = await fetch(url, {
            signal: controller.signal,
            method: 'POST',
            mode: 'cors',
            cache: 'no-cache',
            credentials: 'same-origin',
            headers: {
              authorization: `Bearer ${tokenInfo.refreshToken}`,
            },
          });

          clearTimeout(timeoutId);

          if (response?.status === 200 || response?.status === 201) {
            const tokenInfo = await response.json();
            logger.info('JWT refresh success', tokenInfo);
            this.setTokenInfo(tokenInfo);
            addSessionBreadcrumb('Token refresh succeeded', 'info');
          } else if (response?.status === 401 || response?.status === 403) {
            logger.error(`JWT refresh failed (Error ${response.status}) — token revoked or expired`);
            addSessionBreadcrumb('Token refresh rejected, logging out', 'error', { status: response.status });
            await this.remove();
          } else if (response?.status === 400) {
            logger.error('JWT refresh failed (Error 400)');
            addSessionBreadcrumb('Token refresh rejected, logging out', 'error', { status: 400 });
            await this.remove();
          } else {
            logger.error(`JWT refresh failed (Error ${response?.status})`);
            addSessionBreadcrumb('Token refresh failed', 'error', { status: response?.status });
            throw new Error(`Token refresh failed with status ${response?.status}`);
          }
        } catch (ex) {
          logger.error('JWT refresh failed (Caught)', ex);
          addSessionBreadcrumb('Token refresh threw', 'error', { error: String(ex) });
          // TODO: Add a retry policy if token refresh fails due to network
          // await this.remove();
        }

        this.refreshTokenPromise = null;
      }).bind(this);
    }
    return this.refreshTokenPromise?.();
  }

  getTokenInfo(): JWT | null {
    return this.repository.getJWToken();
  }

  setTokenInfo(tokenInfo: JWT | null): void {
    if (tokenInfo) {
      this.repository.setJWToken(tokenInfo);
    }
  }

  async remove(): Promise<void> {
    return this.repository.logout();
  }

  getAuthorizationConfig<P>(config?: GamRequestConfig<P>): GamRequestConfig<P> {
    if (config) {
      return {
        ...config,
        headers: {
          ...config.headers,
          ...this.repository.setHeadersFromJWT(),
        },
      };
    } else {
      return {
        headers: this.repository.setHeadersFromJWT(),
      };
    }
  }
}
